// Third-party libraries
import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { intComma } from "humanize-plus";

// Project dependencies
import { LoadingBar } from "../components/widgets";
import Histogram from "../components/Histogram";
import { getHeaders, playerUrl, errorHandler } from "../apihelpers";
import { Meta } from "../utilities";
import LoanIcon from "../svg/LoanIcon";
import SortDownIcon from "../svg/SortDownIcon";
import WapIcon from "../svg/WapIcon";
import variables from "../styleVariables";

class Leaders extends Component {
  state = {
    loading: true,
    showAll: false, // Show all for the crawler; all others, show only first 50
    sort: "netpoints", // 'points', 'win_tally','winpct'
    players: null,
  };

  _initialCount = 50; // How many to show at first
  _isMounted = false;

  getLeaders() {
    const headers = getHeaders();
    fetch(playerUrl + "?" + Date.now(), {
      method: "GET",
      headers,
    })
      .then(errorHandler)
      .then((data) => {
        if (this._isMounted) {
          localStorage.setItem("leaders", JSON.stringify(data));
          this.setState({ players: data, loading: false });
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }

  componentDidMount() {
    this._isMounted = true;
    if (localStorage.leaders) {
      this.setState(
        { players: JSON.parse(localStorage.getItem("leaders")) },
        () => this.getLeaders()
      );
    } else {
      this.getLeaders();
    }
  }

  showAllLeaders() {
    this.setState({ showAll: true });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const header = (
      <Fragment>
        <Meta
          title="Rouleur Derby Leaderboard"
          description="These are the current leaders in Rouleur Derby, almost certainly the best fantasy cycling game in the world."
        />
        <h1>Leaders</h1>
        <p className="leaderboard-intro">
          Leaderboard includes only players with at least one bet. Point totals
          do not include loans. The leaderboard can be sorted by points, wins,
          and win percentage.
        </p>
        {this.state.players && this.state.players.length > 20 && (
          <Histogram players={this.state.players} visitor={this.props.player} />
        )}
        <h5>Overall rankings</h5>
        <ul className="leader-notes">
          <li>
            <WapIcon color={variables.colorWap} />
            <span>
              Members of{" "}
              <a
                href="https://www.wideanglepodium.com"
                rel="noopener noreferrer"
                target="_blank"
              >
                Wide Angle Podium
              </a>
            </span>
          </li>
          {this.state.players &&
            this.state.players.filter((player) => player.loaned_points > 0)
              .length > 0 && (
              <li>
                <LoanIcon />
                <span>Players with outstanding loans</span>
              </li>
            )}
        </ul>
        {
          // Hack to get reactsnap to create a PNG of the score history
          navigator.userAgent === "ReactSnap" &&
            this.state.players &&
            this.state.players.map((player, i) => (
              <a key={i} href={`/rd/scorehistory/${player.handle}`}>
                .
              </a>
            ))
        }
      </Fragment>
    );

    if (this.loading) {
      return (
        <Fragment>
          {header}
          <LoadingBar />
        </Fragment>
      );
    }

    var prevPlayer = {}; // Will be used for sorting;
    return (
      <Fragment>
        {header}
        <div className="sticky-table-outer">
          <table className="table--sticky table--rank leaders">
            <thead>
              <tr>
                <th>Player</th>
                <th
                  className={`figure th--sortable ${
                    this.state.sort === "netpoints" && "th--active"
                  }`}
                  onClick={() => this.setState({ sort: "netpoints" })}
                >
                  {this.state.sort === "netpoints" && <SortDownIcon />}Points
                </th>
                <th className="figure">Current Action</th>
                <th className="figure">Resolved Bets</th>
                <th
                  className={`figure th--sortable ${
                    this.state.sort === "wins" && "th--active"
                  }`}
                  onClick={() => this.setState({ sort: "win_tally" })}
                >
                  {this.state.sort === "win_tally" && <SortDownIcon />}Wins
                </th>
                <th
                  className={`figure th--sortable ${
                    this.state.sort === "winpct" && "th--active"
                  }`}
                  onClick={() => this.setState({ sort: "winpct" })}
                >
                  {this.state.sort === "winpct" && <SortDownIcon />}Win %
                </th>
              </tr>
            </thead>
            <tbody>
              {this.state.players &&
                this.state.players
                  .filter((player) => player.bet_tally >= 1) // Only show players with 1 or more bets
                  .map((player) => {
                    if (player.win_tally === 0) {
                      player.winpct = 0;
                    } else {
                      player.winpct =
                        (player.win_tally * 100) /
                        (player.bet_tally - player.current_bets);
                    }
                    return player;
                  })
                  .sort((a, b) => b[this.state.sort] - a[this.state.sort])
                  .map((player, i) => {
                    if (i === 0) {
                      player.rank = 1;
                      prevPlayer = player;
                      return player;
                    } else if (
                      player[this.state.sort] === prevPlayer[this.state.sort]
                    ) {
                      player.rank = "-\u00A0";
                      prevPlayer = player;
                      return player;
                    } else {
                      player.rank = i + 1;
                      prevPlayer = player;
                      return player;
                    }
                  })
                  .slice(
                    0,
                    this.state.showAll
                      ? this.state.players.length
                      : this._initialCount
                  )
                  .map((player, i) => (
                    <PlayerItem
                      key={i}
                      player={player}
                      visitor={this.props.player}
                    />
                  ))}
            </tbody>
          </table>
          {!this.state.showAll &&
            this.state.players &&
            this.state.players.length > this._initialCount && (
              <button type="button" onClick={() => this.showAllLeaders()}>
                Show all
              </button>
            )}
        </div>

        {!this.state.players && <LoadingBar />}
      </Fragment>
    );
  }
}

function PlayerItem({ player, visitor }) {
  return (
    <tr
      className={player.id && player.id === visitor.id ? "rank-row--self" : ""}
    >
      <td>
        <div className="rank-cell">
          <div className="rank-cell__rank">
            {typeof player.rank === "number" ? `${player.rank}.` : player.rank}
          </div>
          <div className="rank-cell__name">
            {player.podium_member && <WapIcon color={variables.colorWap} />}
            <Link to={`/${player.handle}`}>
              {player.screen_name}{" "}
              {player.loaned_points > 0 && (
                <LoanIcon
                  color={
                    player.id && player.id === visitor.id
                      ? "#ffffff"
                      : variables.colorCancel
                  }
                />
              )}
            </Link>
          </div>
        </div>
      </td>
      <td className="figure">{intComma(player.netpoints)}</td>
      <td className="figure">{intComma(player.current_action)}</td>
      <td className="figure">
        {intComma(player.bet_tally - player.current_bets)}
      </td>
      <td className="figure">{intComma(player.win_tally)}</td>
      <td className="figure">
        {player.win_tally > 0 ? Math.round(player.winpct) : 0}%
      </td>
    </tr>
  );
}

Leaders.propTypes = {
  players: PropTypes.array,
};

export default Leaders;
